import BlockRounded from "@mui/icons-material/BlockRounded";
import { Box } from "@mui/material";
import { format } from "date-fns";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  READINESS,
  READINESS_ORDER,
  cardAddress,
  cardAttentionChips,
  isSoftDate,
  isTrade,
  pipelineStatusLabel,
} from "../../components/closingReadiness/closingReadinessUtils";
import { alpha } from "@mui/material/styles";

import { amber, slate, teal } from "../../theme/colors";
import {
  deriveStatus,
  formatCompactDollar,
  loanAmountCompact,
  loanChipLabel,
  loanId,
  statusMetaForLoan,
  sumLoanAmounts,
} from "../LoanClosingsCalendarScreen/closingCalendarUtils";
import { ReadinessBar } from "./CoverageGridView";
import { C, MONO, entryLabel, entryTimeLabel, reasonStyle } from "./coverageUtils";

const Tag = ({ children, warm }) => (
  <Txt
    sx={{
      fontSize: 10,
      fontWeight: 600,
      color: warm ? amber.dark : C.faint,
      border: `1px solid ${warm ? "#fcd9a8" : C.line}`,
      background: warm ? "#fffaf0" : "transparent",
      borderRadius: "5px",
      px: 0.75,
      py: "1px",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {children}
  </Txt>
);

// One funding on the day: edge bar · loan # · sync id · name / address · tags ·
// amount. Tinted when the date is firm, outlined when it is only expected.
const DayRow = ({ card, onClick, selected }) => {
  const s = statusMetaForLoan(card);
  const soft = isSoftDate(card);
  const closed = !!card?.closed;
  const flags = cardAttentionChips(card).filter((f) => !["closer", "closed", "stale"].includes(f.key));
  const sub = [pipelineStatusLabel(card), cardAddress(card) || card?.loanName].filter(Boolean).join(" · ");
  return (
    <Flx
      ac
      g={1.25}
      onClick={() => onClick(card)}
      sx={{
        position: "relative",
        px: 1.5,
        pl: 1.75,
        py: 1.1,
        mb: 0.5,
        borderRadius: "8px",
        background: closed || soft ? "transparent" : s.bg,
        border: `1px solid ${closed || soft ? C.lineSoft : "transparent"}`,
        opacity: closed ? 0.55 : 1,
        cursor: "pointer",
        minWidth: 0,
        transition: "filter 0.1s, background 0.1s",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 5,
          bottom: 5,
          width: 3,
          borderRadius: "3px",
          background: s.c,
        },
        "&:hover": { background: closed || soft ? C.lineSoft : s.bg, filter: closed || soft ? "none" : "brightness(.965)" },
        ...(selected
          ? {
              background: teal[100],
              borderColor: teal[500],
              boxShadow: `0 0 0 2px ${alpha(teal[500], 0.18)}`,
              "&::before": { background: teal[500] },
              "&:hover": { background: teal[100], filter: "none" },
            }
          : {}),
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <Txt sx={{ fontSize: 13, fontWeight: 600, color: C.ink, textDecoration: closed ? "line-through" : "none", whiteSpace: "nowrap" }}>
            {loanChipLabel(card)}
          </Txt>
          {card?.syncLoanId && (
            <Txt sx={{ fontFamily: MONO, fontSize: 11, color: C.faint, whiteSpace: "nowrap" }}>{card.syncLoanId}</Txt>
          )}
          {isTrade(card) && <Tag>TRADE</Tag>}
          {card?.stale && !closed && <Tag warm>STALE · {card?.daysSinceActivity ?? "?"}d</Tag>}
          {soft && !closed && <Tag>expected</Tag>}
          {flags.slice(0, 2).map((f) => (
            <Txt key={f.key} sx={{ fontSize: 10, fontWeight: 600, color: f.c, background: f.bg, borderRadius: "5px", px: 0.75, py: "1px", whiteSpace: "nowrap", flexShrink: 0 }}>
              {f.label}
            </Txt>
          ))}
        </Flx>
        {sub && (
          <Txt sx={{ fontSize: 11.5, color: C.sub, display: "block", mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {sub}
          </Txt>
        )}
      </Box>
      {loanAmountCompact(card) && (
        <Txt sx={{ fontFamily: MONO, fontSize: 12.5, color: C.sub, flexShrink: 0 }}>{loanAmountCompact(card)}</Txt>
      )}
    </Flx>
  );
};

const GroupTitle = ({ color, label, count }) => (
  <Flx ac g={0.75} sx={{ mb: 0.9 }}>
    {color && <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color }} />}
    <Txt sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.sub }}>
      {label}
    </Txt>
    <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: C.faint }}>· {count}</Txt>
  </Flx>
);

// The single-day view: header + readiness bar, then the day's fundings grouped
// by readiness level (worst first, closed last), the team out of office, and
// the holiday / wire-block notice.
const CoverageDayView = ({
  date,
  closings,
  entries,
  holiday,
  layers,
  filterLabel,
  onSelectClosing,
  selectedId,
  onSelectEntry,
  onSelectHoliday,
}) => {
  const shownClosings = layers.closings ? closings : [];
  const shownEntries = layers.coverage ? entries : [];
  const showHoliday = layers.holidays && holiday;
  const active = shownClosings.filter((c) => !c?.closed);
  const closedCount = shownClosings.length - active.length;
  const groups = {};
  shownClosings.forEach((c) => {
    const k = deriveStatus(c);
    (groups[k] = groups[k] || []).push(c);
  });
  const empty = !shownClosings.length && !shownEntries.length && !showHoliday;

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2, sm: 3.25 }, pt: 2.25, pb: 5 }}>
      <Flx ac g={1.5} sx={{ alignItems: "baseline", mb: 0.5 }}>
        <Txt sx={{ fontSize: 21, fontWeight: 700, letterSpacing: "-0.015em", color: C.ink }}>
          {format(date, "EEEE, MMMM d")}
        </Txt>
        <Txt sx={{ fontSize: 13, color: C.sub }}>
          {active.length} {filterLabel || "active"}
          {active.length ? ` · ${formatCompactDollar(sumLoanAmounts(active))}` : ""}
          {closedCount ? ` · ${closedCount} closed` : ""}
          {shownEntries.length ? ` · ${shownEntries.length} out` : ""}
        </Txt>
      </Flx>
      <ReadinessBar closings={shownClosings} height={5} sx={{ maxWidth: 460, my: 1, mb: 2.25 }} />

      {showHoliday && (
        <Flx
          ac
          g={0.75}
          onClick={onSelectHoliday ? () => onSelectHoliday(holiday) : undefined}
          sx={{
            maxWidth: 760,
            mb: 2.25,
            px: 1.5,
            py: 1,
            borderRadius: "8px",
            background: holiday?.wireBlocked ? C.blockBg : C.lineSoft,
            color: holiday?.wireBlocked ? C.block : C.sub,
            cursor: onSelectHoliday ? "pointer" : "default",
          }}
        >
          {holiday?.wireBlocked && <BlockRounded sx={{ fontSize: 15 }} />}
          <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: "inherit" }}>
            {holiday?.wireBlocked ? `Wires blocked · ${holiday?.name || "Bank holiday"}` : holiday?.name || "Holiday"}
          </Txt>
        </Flx>
      )}

      {[...READINESS_ORDER].map((k) =>
        groups[k] ? (
          <Box key={k} sx={{ mb: 2.25, maxWidth: 760 }}>
            <GroupTitle color={READINESS[k].c} label={READINESS[k].label} count={groups[k].length} />
            {groups[k].map((card) => (
              <DayRow key={loanId(card)} card={card} onClick={onSelectClosing} selected={selectedId != null && loanId(card) === selectedId} />
            ))}
          </Box>
        ) : null,
      )}

      {shownEntries.length > 0 && (
        <Box sx={{ mb: 2.25, maxWidth: 760 }}>
          <GroupTitle label="Out of office" count={shownEntries.length} />
          {shownEntries.map((entry) => {
            const s = reasonStyle(entry?.reason);
            return (
              <Flx
                ac
                g={1.25}
                key={entry._id}
                onClick={() => onSelectEntry(entry)}
                sx={{
                  position: "relative",
                  px: 1.5,
                  pl: 1.75,
                  py: 1.1,
                  mb: 0.5,
                  borderRadius: "8px",
                  background: s.bg,
                  cursor: "pointer",
                  "&::before": { content: '""', position: "absolute", left: 0, top: 5, bottom: 5, width: 3, borderRadius: "3px", background: s.c },
                  "&:hover": { filter: "brightness(.965)" },
                }}
              >
                <Txt sx={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{entryLabel(entry)}</Txt>
                {entry?.reason && <Tag>{entry.reason}</Tag>}
                {entry?.backupFullName && (
                  <Txt sx={{ fontSize: 11.5, color: C.sub }}>backup · {entry.backupFullName}</Txt>
                )}
                <Txt sx={{ ml: "auto", fontFamily: MONO, fontSize: 12, color: C.sub, flexShrink: 0 }}>
                  {entryTimeLabel(entry)}
                </Txt>
              </Flx>
            );
          })}
        </Box>
      )}

      {empty && (
        <Txt sx={{ fontSize: 13, color: slate[400] }}>
          Nothing {filterLabel ? "matches the current filter" : "scheduled"} on this day.
        </Txt>
      )}

    </Box>
  );
};

export default CoverageDayView;
